import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AddIcon } from "../../icons";
import { AutomationStore, type OverrideRow } from "./AutomationStore";
import { localDateTime } from "../common/localDateTime";

/**
 * Editor de correcciones de trámite (SnapshotOverride) de una edición:
 * lista auditable (quién/cuándo/por qué, activable) + alta {path, value, note}.
 * Las correcciones se aplican sobre data_ congelado al ARMAR los trámites
 * (DNDA/SADAIC) — el contrato firmado jamás se modifica.
 */
export const OverridesEditor = observer(
  ({ store, editionId }: { store: AutomationStore; editionId: string }) => {
    const [path, setPath] = useState("");
    const [value, setValue] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    useEffect(() => {
      void store.loadOverrides(editionId);
    }, [store, editionId]);

    const overrides = store.overridesByEdition[editionId] ?? [];

    const submit = async () => {
      setFormError(null);
      if (!path.trim() || !note.trim()) {
        setFormError("Path y motivo son obligatorios.");
        return;
      }
      // El valor se interpreta como JSON si parsea (números, objetos, listas,
      // booleanos); si no, queda como string plano.
      let parsed: unknown = value;
      try {
        parsed = JSON.parse(value);
      } catch {
        /* string plano */
      }
      setSaving(true);
      try {
        await store.createOverride(editionId, path.trim(), parsed, note.trim());
        setPath("");
        setValue("");
        setNote("");
      } catch {
        /* error ya reflejado en store.error */
      } finally {
        setSaving(false);
      }
    };

    return (
      <Stack spacing={1.5}>
        <Alert severity="info" sx={{ py: 0.5 }}>
          Las correcciones se aplican al armar el trámite (DNDA/SADAIC) <b>sin tocar el
          contrato firmado</b>. Path sobre los datos del contrato, p.ej.{" "}
          <code>works[0].authors[1].taxId</code>. Crear claves nuevas o agregar elementos
          (<code>authors[2]</code> con un objeto) también funciona.
        </Alert>

        {overrides.length > 0 && (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Campo (path)</TableCell>
                  <TableCell>Valor corregido</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell>Quién / cuándo</TableCell>
                  <TableCell align="center">Activa</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overrides.map((o: OverrideRow) => (
                  <TableRow key={o.id} hover sx={{ opacity: o.active ? 1 : 0.5 }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {o.path}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>
                      <Tooltip title={JSON.stringify(o.value)}>
                        <Typography variant="body2" noWrap sx={{ fontFamily: "monospace" }}>
                          {JSON.stringify(o.value)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Tooltip title={o.note}>
                        <Typography variant="body2" noWrap>
                          {o.note}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {o.createdBy_?.email ?? "—"} · {localDateTime(o.createdAt_)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={o.active}
                        onChange={(e) => void store.setOverrideActive(editionId, o, e.target.checked)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Nueva corrección
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="flex-start">
            <TextField
              label="Campo (path sobre el contrato)"
              size="small"
              sx={{ minWidth: 280 }}
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder="works[0].authors[1].taxId"
            />
            <TextField
              label="Valor corregido"
              size="small"
              sx={{ minWidth: 200 }}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder='20-12345678-9 o {"…": …}'
            />
            <TextField
              label="Motivo (obligatorio)"
              size="small"
              sx={{ flex: 1, minWidth: 240 }}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="CUIT rechazado por la DNDA"
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              disabled={saving}
              onClick={() => void submit()}
            >
              {saving ? "…" : "Agregar"}
            </Button>
          </Stack>
          {formError && (
            <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
              {formError}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Paths frecuentes:
            </Typography>
            {["works[0].authors[0].taxId", "works[0].title", "works[0].authors[0].firstName"].map((p) => (
              <Chip key={p} label={p} size="small" variant="outlined" onClick={() => setPath(p)} sx={{ fontFamily: "monospace" }} />
            ))}
          </Stack>
        </Box>
      </Stack>
    );
  }
);
